import { Controller } from '@hotwired/stimulus';
import { queueDisplaySave } from 'helpers/display_saves';
import { OPTIONS, DEFAULTS, RANGES, RANGE, valueName } from './goal_graph_options_controller';

// The page-wide gear on a progress report's goal region: the same four switches as the gear on
// each goal's band (goal-graph-options), applied to every goal on the page at once, and beneath
// them the dates every goal's chart covers: This period, All, or dates typed into the fields. A
// master switch, pick or date writes its value onto every goal card's values (each card applies
// the change itself, through goal-graph-options, without saving) and saves every card's options
// to the report in one request (PATCH, JSON) when the element carries a url. The page has changed
// by then, so a save that does not go through (a session that ended, a change the report refused)
// is said on the page (the notice target) rather than left for the PDF to contradict; a goal's own
// gear reports its failed save the same way (`goal-graph-options:save-failed` bubbles up here).
//
// A master reads as on while every goal has the option on, off while none has, and
// indeterminate for a mix; a pick reads as pressed while every goal is on that range, and the
// fields show the dates every goal shares, or the report's own when the goals differ. They all
// re-sync when a goal's own gear is used (the card announces `goal-graph-options:changed`, which
// bubbles up to this element). Mounted on the element that wraps the heading and the goal cards.
//
// Targets:
//   switch    a master checkbox, carrying data-option (an OPTIONS key)
//   pick      a range button, carrying data-range ('period' | 'all')
//   fromInput the From date field
//   toInput   the To date field
//   rangeNote whose dates are in force
//   notice    the alert shown once a save has failed (hidden until then)
// Values:
//   url                   where to save (absent when the viewer may not change the report)
//   reportFrom, reportTo  the report's own dates, ISO
export default class extends Controller {
  static targets = ['switch', 'pick', 'fromInput', 'toInput', 'rangeNote', 'notice'];
  static values = { url: String, reportFrom: { type: String, default: '' }, reportTo: { type: String, default: '' } };

  connect() {
    this.sync();
  }

  // A master switch: its option onto every goal card, saved together.
  change(event) {
    const { option } = event.target.dataset;
    if (!OPTIONS.includes(option)) return;
    this.write({ [option]: event.target.checked });
  }

  // This period or All, onto every goal card.
  pickRange(event) {
    const { range } = event.currentTarget.dataset;
    if (!['period', 'all'].includes(range)) return;
    this.write({ range, from: '', to: '' });
  }

  // Dates typed into the master fields, onto every goal card: dates of their own, unless they are
  // the report's, which is the reporting period. A pair that ends before it starts, or half a
  // pair, is put back the way it was.
  changeRange() {
    const from = this.hasFromInputTarget ? this.fromInputTarget.value : '';
    const to = this.hasToInputTarget ? this.toInputTarget.value : '';
    if (!from || !to || from > to) {
      this.sync();
      return;
    }
    if (from === this.reportFromValue && to === this.reportToValue) this.write({ range: 'period', from: '', to: '' });
    else this.write({ range: 'custom', from, to });
  }

  // The given values onto every card, then one save for all of them.
  write(values) {
    const graphs = {};
    this.cards().forEach((card) => {
      Object.entries(values).forEach(([key, value]) => {
        card.dataset[this.valueKey(key)] = String(value);
      });
      const goal = card.dataset.goalGraphOptionsGoalValue;
      if (goal) graphs[goal] = this.optionsOf(card);
    });
    this.sync();
    this.save(graphs);
  }

  // The masters as the cards stand.
  sync() {
    const states = this.cards().map((card) => this.optionsOf(card));
    this.switchTargets.forEach((element) => {
      const { option } = element.dataset;
      if (!OPTIONS.includes(option)) return;
      const all = states.length > 0 && states.every((state) => state[option]);
      const none = states.every((state) => !state[option]);
      element.checked = all;
      element.indeterminate = !all && !none;
    });

    // The range: a pick reads as pressed while every goal is on it; the fields hold the dates the
    // goals share, or the report's own.
    const shared = (key) => {
      const values = new Set(states.map((state) => state[key] || ''));
      return values.size === 1 ? [...values][0] : '';
    };
    const range = shared('range');
    const from = shared('from');
    const to = shared('to');
    this.pickTargets.forEach((element) => {
      const active = states.length > 0 && element.dataset.range === range;
      element.classList.toggle('active', active);
      element.setAttribute('aria-pressed', String(active));
    });
    if (this.hasFromInputTarget) {
      this.fromInputTarget.value = from || this.reportFromValue;
      this.fromInputTarget.max = to || this.reportToValue;
    }
    if (this.hasToInputTarget) {
      this.toInputTarget.value = to || this.reportToValue;
      this.toInputTarget.min = from || this.reportFromValue;
    }
    this.rangeNoteTargets.forEach((element) => {
      if (states.length > 0 && !range)
        element.textContent = 'The goals differ; a pick or a date here applies to every goal.';
      else if (range === 'all') element.textContent = 'Every signed session, on every goal.';
      else if (range === 'custom') element.textContent = 'Chosen dates, on every goal.';
      else element.textContent = "Applied to every goal; the report's dates to begin with.";
    });
  }

  // The bands of the goal cards on show; a card the goal picker hides is left out until it shows.
  cards() {
    return Array.from(this.element.querySelectorAll('[data-controller~="goal-graph-options"]')).filter(
      (element) => !element.closest('[data-goals-report-goal][hidden]')
    );
  }

  // A card's four options (an option never written is at its default), its range ('period'
  // unless written) and its dates (null for none).
  optionsOf(card) {
    const options = OPTIONS.reduce((all, option) => {
      const value = card.dataset[this.valueKey(option)];
      return { ...all, [option]: value === undefined ? DEFAULTS[option] : value !== 'false' };
    }, {});
    const range = card.dataset[this.valueKey('range')];
    options.range = RANGES.includes(range) ? range : 'period';
    RANGE.forEach((end) => {
      options[end] = options.range === 'custom' ? card.dataset[this.valueKey(end)] || null : null;
    });
    return options;
  }

  // The dataset key of a card's value for an option.
  valueKey(option) {
    const name = valueName(option);
    return `goalGraphOptions${name.charAt(0).toUpperCase()}${name.slice(1)}Value`;
  }

  // One at a time with the page's other display saves (helpers/display_saves).
  save(graphs) {
    if (!this.hasUrlValue || !this.urlValue) return;
    const url = this.urlValue;
    queueDisplaySave(() => {
      const token = document.querySelector('meta[name="csrf-token"]');
      return fetch(url, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          'X-CSRF-Token': token ? token.getAttribute('content') : '',
        },
        credentials: 'same-origin',
        body: JSON.stringify({ graphs }),
      });
    })
      .then((response) => {
        // A redirect is the sign-in page: the session ended and nothing was saved.
        if (!response.ok || response.redirected) throw new Error(`Graph options not saved (${response.status})`);
      })
      .catch(() => this.failed());
  }

  // The page and the report now differ; say so until the page is reloaded. Also the action for a
  // goal's own gear announcing the same.
  failed() {
    this.noticeTargets.forEach((element) => {
      element.hidden = false;
    });
  }
}
